import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { FormCheckbox, FormField, FormSelect } from '../../components/FormField'
import { Breadcrumbs, type Crumb } from '../../components/Breadcrumbs'
import { ActivationPanel } from '../../components/ActivationPanel'
import { RecordLink } from '../../components/RecordLink'
import { FleetAvailabilityNote } from './FleetStandingCell'
import type { CarrierStanding, FleetStanding } from './fleetStanding'
import { DocumentsPanel } from '../documents/DocumentsPanel'
import { apiErrorMessage } from '../../lib/errors'
import { keepDecimal } from '../../lib/number'
import { parseAmount } from '../../lib/number'
import {
  addTrailer,
  featuresAllowedOn,
  getCarrier,
  updateTrailer,
  TRAILER_TYPES,
  type EquipmentFeature,
  type EquipmentType,
  type TrailerPayload,
} from './api'
import {
  messageFor,
  toTrailerFormErrors,
  withoutTrailerErrorsFor,
  type FormErrors,
} from './errors'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'

const emptyForm = {
  trailerNumber: '',
  type: 'DryVan',
  vin: '',
  palletCapacity: '',
  weightCapacity: '',
}

type TrailerForm = typeof emptyForm

const noErrors: FormErrors = { fields: {}, summary: [] }

/**
 * Adds or edits a trailer — the record Stage 2's availability posting reads its
 * capacities from. Empty capacities travel as "not recorded", exactly like an
 * unrecorded bond.
 */
export function TrailerFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: carrierId, trailerId } = useParams()
  const isEditing = trailerId !== undefined

  const [form, setForm] = useState<TrailerForm>(emptyForm)
  // What the trailer states it has besides its type (TRKC-100) — a set, beside the text fields.
  const [features, setFeatures] = useState<EquipmentFeature[]>([])
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The reading this form was opened on. Set when the record loads and by "load what is
  // saved" — never by the standing re-read below, which would adopt somebody else's
  // version and let the next save overwrite what they wrote.
  const [version, setVersion] = useState<string | null>(null)
  // The trail's names (TRKC-97): the carrier's, and this record's as it is saved —
  // never as it is being typed.
  const [carrierName, setCarrierName] = useState<string | null>(null)
  const [savedName, setSavedName] = useState<string | null>(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)
  // Whether we still work with this one. Its own fact, stated by its own button --
  // never a field that rides along with a save of the name and the phone.
  // The carrier's half of the answer. Held rather than inferred: a record whose own
  // papers are incomplete under a carrier we stood down is out of play twice over,
  // and the two are indistinguishable from the child's own fields.
  const [standing, setStanding] = useState<{
    record: FleetStanding
    carrier: CarrierStanding
  } | null>(null)
  const [activation, setActivation] = useState<{ active: boolean; deactivatedAt: string | null }>({
    active: true,
    deactivatedAt: null,
  })

  // The re-read in flight: only the latest may write (see DriverFormPage).
  const refreshGeneration = useRef(0)
  // A re-read that failed — the form stays, the note goes (see DriverFormPage).
  const [standingFailure, setStandingFailure] = useState<{ error: unknown } | null>(null)

  /**
   * The availability note is the server's conclusion, re-read after every write that
   * can move it — an activation change, a paper filed or struck in the panel below —
   * and never guessed at. Re-read on its own, without the form: the trailer's fields
   * did not change, and a capacity half typed must not be thrown away because an
   * inspection was filed underneath it (review finding on #13).
   */
  const refreshStanding = useCallback(async () => {
    if (carrierId === undefined || trailerId === undefined) {
      return
    }

    refreshGeneration.current += 1
    const generation = refreshGeneration.current
    setStandingFailure(null)

    try {
      const carrier = await getCarrier(carrierId)
      if (generation !== refreshGeneration.current) {
        return
      }

      const trailer = carrier.trailers.find((row) => row.id === trailerId)
      if (trailer === undefined) {
        setLoadFailure({ error: null })
        return
      }

      setActivation({ active: trailer.active, deactivatedAt: trailer.deactivatedAt })
      setStanding({
        record: trailer,
        carrier,
      })
    } catch (caught) {
      if (generation === refreshGeneration.current) {
        setStanding(null)
        setStandingFailure({ error: caught })
      }
    }
  }, [carrierId, trailerId])

  useEffect(() => {
    if (carrierId === undefined || trailerId === undefined) {
      return
    }

    let active = true
    refreshGeneration.current += 1
    setStandingFailure(null)
    setIsLoading(true)
    setLoadFailure(null)

    void (async () => {
      try {
        const carrier = await getCarrier(carrierId)
        const trailer = carrier.trailers.find((row) => row.id === trailerId)
        if (!active) {
          return
        }

        if (trailer === undefined) {
          setLoadFailure({ error: null })
          return
        }

        setActivation({ active: trailer.active, deactivatedAt: trailer.deactivatedAt })
        setStanding({
          record: trailer,
          carrier,
        })
        setVersion(trailer.updatedAt)
        setCarrierName(carrier.name)
        setSavedName(trailer.trailerNumber)
        setForm({
          trailerNumber: trailer.trailerNumber,
          type: trailer.type,
          vin: trailer.vin ?? '',
          palletCapacity: trailer.palletCapacity === null ? '' : String(trailer.palletCapacity),
          weightCapacity:
            trailer.weightCapacityPounds === null ? '' : String(trailer.weightCapacityPounds),
        })
        setFeatures(trailer.features)
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [carrierId, trailerId])

  // A new trailer reads nothing else, so its carrier's name is asked for the trail
  // alone. Unread, the step shows an ellipsis: the form works the same without it.
  useEffect(() => {
    if (carrierId === undefined || isEditing) {
      return
    }

    let active = true

    getCarrier(carrierId)
      .then((carrier) => {
        if (active) {
          setCarrierName(carrier.name)
        }
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [carrierId, isEditing])

  if (carrierId === undefined) {
    return null
  }

  function set(field: keyof TrailerForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => withoutTrailerErrorsFor(current, field))

    // A feature the new type cannot have goes with the old type: a vent leaves with the van.
    if (field === 'type') {
      const allowed = featuresAllowedOn(value as EquipmentType)
      setFeatures((current) => current.filter((feature) => allowed.includes(feature)))
    }
  }

  function toggleFeature(feature: EquipmentFeature, checked: boolean) {
    setFeatures((current) =>
      checked ? [...current, feature] : current.filter((existing) => existing !== feature),
    )
    setErrors((current) => withoutTrailerErrorsFor(current, 'features'))
  }

  /**
   * The deliberate step after a refusal: this trailer as it is stored now, into this
   * form. Nothing else on the page is reloaded, and the version comes with it.
   */
  async function loadWhatIsSaved() {
    if (carrierId === undefined || trailerId === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const carrier = await getCarrier(carrierId)
      const trailer = carrier.trailers.find((row) => row.id === trailerId)

      if (trailer === undefined) {
        setLoadFailure({ error: null })
        return
      }

      setVersion(trailer.updatedAt)
      setCarrierName(carrier.name)
      setSavedName(trailer.trailerNumber)
      setForm({
        trailerNumber: trailer.trailerNumber,
        type: trailer.type,
        vin: trailer.vin ?? '',
        palletCapacity: trailer.palletCapacity === null ? '' : String(trailer.palletCapacity),
        weightCapacity:
          trailer.weightCapacityPounds === null ? '' : String(trailer.weightCapacityPounds),
      })
      setFeatures(trailer.features)
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toTrailerFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)

    // Empty means "not recorded"; anything typed has to be a real number before it
    // travels — the pallet count a whole one. The 1-60 and weight ranges are the
    // domain's call, answered with its own codes.
    const pallets = form.palletCapacity.trim() === '' ? null : parseAmount(form.palletCapacity)
    if (pallets !== null && !Number.isInteger(pallets)) {
      setErrors({ fields: { palletCapacity: 'invalid_pallets' }, summary: [] })
      return
    }
    if (form.palletCapacity.trim() !== '' && pallets === null) {
      setErrors({ fields: { palletCapacity: 'invalid_pallets' }, summary: [] })
      return
    }

    const weight = form.weightCapacity.trim() === '' ? null : parseAmount(form.weightCapacity)
    if (form.weightCapacity.trim() !== '' && weight === null) {
      setErrors({ fields: { weightCapacity: 'invalid_weight' }, summary: [] })
      return
    }

    const payload: TrailerPayload = {
      trailerNumber: form.trailerNumber.trim(),
      type: form.type,
      vin: blankToNull(form.vin),
      palletCapacity: pallets,
      weightCapacityPounds: weight,
      features,
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      if (trailerId === undefined) {
        await addTrailer(carrierId!, payload)
      } else {
        await updateTrailer(carrierId!, trailerId, payload)
      }

      void navigate(`/carriers/${carrierId}`, { replace: true })
    } catch (caught) {
      setErrors(toTrailerFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  /** Carriers › the carrier › this record — or the page's own title when the record cannot be read. */
  const trail = (leaf: string | null): Crumb[] => [
    { label: t('nav.carriers'), to: '/carriers' },
    { label: carrierName, to: `/carriers/${carrierId}` },
    { label: leaf },
  ]

  if (isLoading) {
    return <p className="session-check">{t('carriers.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs trail={trail(t('carriers.trailerEditTitle'))} />
        <header className="page__header">
          <h1>{t('carriers.trailerEditTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'carriers.loadError')}
        </p>
        <p>
          <Link to={`/carriers/${carrierId}`}>{t('carriers.backToCarrier')}</Link>
        </p>
      </section>
    )
  }

  // A refusal that names a record links to it: the trailer that already has the number (TRKC-105).
  const field = (name: keyof TrailerForm | 'features'): ReactNode => {
    const code = errors.fields[name]

    if (code === undefined) {
      return undefined
    }

    const subject = errors.subjects?.[code]
    const sentence = messageFor(code, t, 'carriers.saveError', subject)

    return subject === undefined ? (
      sentence
    ) : (
      <>
        {sentence} <RecordLink subject={subject} />
      </>
    )
  }

  // This trailer as it is saved, or the step a new one takes.
  const leaf = !isEditing
    ? t('breadcrumbs.newTrailer')
    : savedName === null
      ? null
      : t('breadcrumbs.trailer', { number: savedName })

  return (
    <section className="page">
      <Breadcrumbs trail={trail(leaf)} />
      <header className="page__header">
        <h1>{isEditing ? t('carriers.trailerEditTitle') : t('carriers.trailerTitle')}</h1>
      </header>

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice onLoadSaved={() => void loadWhatIsSaved()} isLoading={isLoadingSaved} />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{messageFor(code, t, 'carriers.saveError')}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset">
          <legend>{t('carriers.identity')}</legend>

          <FormField
            name="trailerNumber"
            label={t('carriers.trailerNumber')}
            value={form.trailerNumber}
            onChange={(value) => set('trailerNumber', value)}
            error={field('trailerNumber')}
            maxLength={32}
            required
            width="narrow"
          />
          <FormSelect
            name="type"
            label={t('carriers.type')}
            value={form.type}
            onChange={(value) => set('type', value)}
            options={TRAILER_TYPES.map((type) => ({
              value: type,
              label: t(`carriers.types.${type}`),
            }))}
            error={field('type')}
            required
          />
          <FormField
            name="vin"
            label={t('carriers.vin')}
            value={form.vin}
            // Uppercased as typed — the screen shows the spelling the domain stores.
            onChange={(value) => set('vin', value.toUpperCase())}
            error={field('vin')}
            maxLength={17}
          />
          <FormField
            name="palletCapacity"
            label={t('carriers.palletCapacity')}
            value={form.palletCapacity}
            // Letters and symbols never land; a stray dot does, so the honest
            // "whole number of pallets" complaint can still be raised at save.
            onChange={(value) => set('palletCapacity', keepDecimal(form.palletCapacity, value))}
            error={field('palletCapacity')}
            inputMode="decimal"
            width="narrow"
          />
          <FormField
            name="weightCapacity"
            label={t('carriers.weightCapacity')}
            value={form.weightCapacity}
            onChange={(value) => set('weightCapacity', keepDecimal(form.weightCapacity, value))}
            error={field('weightCapacity')}
            inputMode="decimal"
            width="narrow"
          />
          {/* Only what this type can have is offered (TRKC-100); the server judges every save. */}
          <div className="field field--choices" role="group" aria-labelledby="features-label">
            <span id="features-label">{t('carriers.features')}</span>
            {featuresAllowedOn(form.type as EquipmentType).map((feature) => (
              <FormCheckbox
                key={feature}
                name={`feature-${feature}`}
                label={t(`equipment.features.${feature}`)}
                checked={features.includes(feature)}
                onChange={(checked) => toggleFeature(feature, checked)}
              />
            ))}
            <span className="field__hint">{t('carriers.featuresHint')}</span>
            {field('features') !== undefined && (
              <span className="field__error" role="alert">
                {field('features')}
              </span>
            )}
          </div>
        </fieldset>

        <div className="form__actions">
          <Link className="button button--quiet" to={`/carriers/${carrierId}`}>
            {t('carriers.backToCarrier')}
          </Link>
          <button type="submit" className="button" disabled={isSubmitting}>
            {isSubmitting ? t('carriers.saving') : t('carriers.saveTrailer')}
          </button>
        </div>
      </form>

      {/* Registration and inspection papers live on the trailer — and filing one can
          move its standing, so the note below is re-read when it does. */}
      {trailerId !== undefined && (
        <DocumentsPanel
          ownerType="Trailer"
          ownerId={trailerId}
          onOwnerStandingChanged={() => void refreshStanding()}
        />
      )}
      {trailerId !== undefined && (
        <>
          {standing !== null && (
            <FleetAvailabilityNote
              record={standing.record}
              carrier={standing.carrier}
            />
          )}
          {standingFailure !== null && (
            <p className="alert alert--warning" role="status">
              {apiErrorMessage(standingFailure.error, t, 'compliance.standingUnread')}
            </p>
          )}
          <ActivationPanel
            ownerType="Trailer"
            ownerId={trailerId}
            active={activation.active}
            deactivatedAt={activation.deactivatedAt}
            onChanged={(active, deactivatedAt) => {
              setActivation({ active, deactivatedAt })
              void refreshStanding()
            }}
          />
        </>
      )}
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
